import {
  PAGE_SIZE,
  PageSizeParams,
  withOverflowFlexGrowClass,
} from '../constants';
import t from 'react-translate';
import { css } from '@emotion/react';
import LoadingRow from './loading-row';
import { Button } from 'react-bootstrap';
import { useAppDispatch } from 'redux/store';
import NvIcon from 'shared/components/nv-icon';
import MentoringProgramContext from '../context';
import { config } from '@config/pendo.config.json';
import { ColumnID } from './custom-communication-row/types';
import NvTable, { Column, RowProps } from 'shared/components/nv-table';
import { gray2, gray7 } from 'styles/global_defaults/colors';
import { ROW_HEIGHT } from './custom-communication-row/styles';
import CustomCommunicationRow from './custom-communication-row';
import { usePaginatedApi } from 'shared/hooks/use-paginated-api';
import useInfiniteScroll from 'shared/hooks/use-infinite-scroll';
import CustomCommunicationModal from './custom-communication-modal';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as EmptyFields } from 'styles/icons/empty-fields.svg';
import { getCustomCommunications } from 'redux/actions/mentorship-program-custom-communications';
import { selectCustomCommunications } from 'redux/selectors/mentorship-program-custom-communications';
import { MentorshipProgramCustomCommunication } from 'redux/schemas/models/mentorship-program-custom-communications';
import { useSelector } from 'react-redux';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { getInstitutionData } from 'redux/actions/institutions';

const STATUS_WIDTH = 50;
const OPTIONS_WIDTH = 56;

export enum ModalMode {
  ADD = 'add',
  VIEW = 'view',
  EDIT = 'edit',
  COPY = 'copy',
}

export type CommunicationModalState = {
  mode: ModalMode | null;
  communicationId: number | null;
  previousMode?: ModalMode | null;
};

const styles = (scrollEnabled: boolean) => css`
  .table-body {
    overflow-y: ${scrollEnabled ? 'auto' : 'hidden'};

    .empty-ui {
      height: 250px;
      gap: 12px;
      color: ${gray2};
      background-color: ${gray7};
    }
  }
`;

type CustomCommunicationsProps = {
  onScroll: React.ComponentProps<'div'>['onScroll'];
  scrollEnabled: boolean;
};

const CustomCommunications = ({
  onScroll,
  scrollEnabled,
}: CustomCommunicationsProps) => {
  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id: programId } = mentoringProgram || {};
  const { id: institutionId } = useSelector(getCurrentInstitution) || {};
  const [modalState, setModalState] = useState<CommunicationModalState>({
    mode: null,
    communicationId: null,
  });
  const scrollRef = useRef<HTMLDivElement>();
  const bodyProps = {
    onScroll,
    ref: scrollRef,
    className: `table-body ${withOverflowFlexGrowClass}`,
  };
  // Detect when there is less than 3 rows, in order to load more
  const reachedEnd = useInfiniteScroll(scrollRef.current, ROW_HEIGHT * 3);
  const translationBase = t.MENTORING_PROGRAMS.COMMUNICATIONS;
  const dataQATag =
    config.pendo.athena.mentorshipProgram.communications.addCommunicationButton;

  const communicationsTableColumns: Column<MentorshipProgramCustomCommunication>[] =
    useMemo(
      () => [
        {
          id: ColumnID.MESSAGE_TYPE,
          accessorFn: (row) => row.type,
          header: translationBase.TABLE.COLUMNS.MESSAGE_TYPE(),
          gridTemplateColumn: '1fr',
        },
        {
          id: ColumnID.SUBJECT,
          accessorFn: (row) => row.subject,
          header: translationBase.TABLE.COLUMNS.SUBJECT(),
          gridTemplateColumn: '2fr',
        },
        {
          id: ColumnID.TO,
          accessorFn: (row) => row.target,
          header: translationBase.TABLE.COLUMNS.TO(),
          gridTemplateColumn: '1fr',
        },
        {
          id: ColumnID.STATUS,
          accessorFn: (row) => row.enabled,
          header: translationBase.TABLE.COLUMNS.STATUS(),
          gridTemplateColumn: `${STATUS_WIDTH}px`,
        },
        {
          id: ColumnID.OPTIONS,
          gridTemplateColumn: `${OPTIONS_WIDTH}px`,
        },
      ],
      []
    );

  const params = useMemo(
    () => ({
      pageSize: PAGE_SIZE,
    }),
    []
  );

  const { result, reset, loadMore, isLoading, page } = usePaginatedApi<
    MentorshipProgramCustomCommunication,
    PageSizeParams
  >(
    (p) =>
      dispatch(getCustomCommunications({ programId, ...p })).then(
        (action) => action.payload
      ),
    params,
    selectCustomCommunications
  );

  const data = useMemo(() => {
    if (isLoading && page === 1) return [];
    return result ?? [];
  }, [isLoading, page, result]);

  useEffect(() => {
    if (reachedEnd) {
      loadMore();
    }
  }, [loadMore, reachedEnd]);

  useEffect(() => {
    if (institutionId) {
      dispatch(getInstitutionData({ institutionId }));
    }
  }, [dispatch, institutionId]);

  const RowComponent = useCallback(
    (props: RowProps<MentorshipProgramCustomCommunication>) => (
      <CustomCommunicationRow {...props} setModalState={setModalState} />
    ),
    [setModalState],
  );

  return (
    <>
      <div
        css={styles(scrollEnabled)}
        className={`d-flex flex-column ${withOverflowFlexGrowClass}`}
      >
        <div className='d-flex justify-content-end mb-4'>
          <Button
            variant='primary'
            onClick={() => setModalState({ mode: ModalMode.ADD, communicationId: null })}
            size='lg'
            className='d-flex flex-row align-items-center'
            aria-label={translationBase.ADD_COMMUNICATION()}
            data-qa={dataQATag}
          >
            <NvIcon icon='create-new-post' size='smallest' className='mr-2' />
            {translationBase.ADD_COMMUNICATION()}
          </Button>
        </div>
        <NvTable<MentorshipProgramCustomCommunication>
          data={data}
          RowComponent={RowComponent}
          className={`d-flex flex-column ${withOverflowFlexGrowClass}`}
          bodyProps={bodyProps}
          columns={communicationsTableColumns}
          meta={{ reset, setModalState }}
          renderAfterRows={(afterRowsProps) => {
            if (result !== null && !result.length) {
              return (
                <div className='d-flex flex-column align-items-center justify-content-center empty-ui'>
                  <EmptyFields width={50} height={50} />
                  <div className='text-medium text-align-center'>
                    {translationBase.TABLE.EMPTY()}
                  </div>
                </div>
              );
            }

            return isLoading && <LoadingRow {...afterRowsProps} />;
          }}
        />
      </div>
      <CustomCommunicationModal
        modalState={modalState}
        setModalState={setModalState}
        reset={reset}
      />
    </>
  );
};

export default CustomCommunications;
